import React, { useEffect, useState } from 'react';
import { machineryService } from '../../services';
import {
  Wrench, Search, Plus, X, Settings, AlertTriangle,
  CheckCircle, Edit2, Fuel, Activity, Building2,
  Download, Star, Check
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────
const STATUS_COLORS: Record<string,string> = {
  AVAILABLE:   'bg-green-100 text-green-700',
  ON_SITE:     'bg-blue-100 text-blue-700',
  MAINTENANCE: 'bg-amber-100 text-amber-700',
  RETIRED:     'bg-gray-100 text-gray-500',
};
const MCH_TYPES = ['JCB','CONCRETE_MIXER','TRANSIT_MIXER','CONCRETE_PUMP','TOWER_CRANE',
  'MOBILE_CRANE','VIBRATOR','COMPACTOR','GENERATOR','WATER_TANKER',
  'DEWATERING_PUMP','DUMPER','EXCAVATOR','ROAD_ROLLER','PILING_RIG','OTHER'];
const SERVICE_TYPES = ['SCHEDULED','BREAKDOWN','INSPECTION','ANNUAL_SERVICE'];
const VND_PAYMENT_TERMS = ['NET_15','NET_30','ADVANCE','WEEKLY','ON_COMPLETION'];

const inp  = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inpRO = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed";
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtN = (n: any) => n ? Number(n).toLocaleString() : '0';

// ── Export CSV ────────────────────────────────────────────────
const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k => `"${String(row[k]??'').replace(/"/g,'""')}"`).join(',')
  )].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = filename; a.click();
};

// ── Star Rating ───────────────────────────────────────────────
const StarRating = ({ value, onChange, readonly=false }: {
  value: number; onChange?: (v:number)=>void; readonly?: boolean;
}) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(s => (
      <button key={s} type="button" onClick={() => !readonly && onChange?.(s)}
        className={readonly ? 'cursor-default' : 'cursor-pointer'}>
        <Star size={13} className={s<=value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// ── Add/Edit Machine Modal ────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const MachineModal = ({ machine, onClose, onSaved }: {
  machine?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!machine;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    mch_code:            machine?.mch_code            || '',
    mch_name:            machine?.mch_name            || '',
    mch_type:            machine?.mch_type            || 'JCB',
    make:                machine?.make                || '',
    model:               machine?.model               || '',
    owned_or_rented:     machine?.owned_or_rented     || 'OWNED',
    capacity_spec:       machine?.capacity_spec       || '',
    registration_number: machine?.registration_number || '',
    current_status:      machine?.current_status      || 'AVAILABLE',
    is_active:           machine?.is_active !== undefined ? machine.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f => ({...f,[k]:v}));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) { await api.put(`/machinery/${machine.mch_id}`, form); }
      else        { await api.post('/machinery/', form); }
      onSaved();
    } catch (err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Machine' : 'Add Machine'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Code {!isEdit && <span className="text-gray-400 font-normal">(auto-generated)</span>}
              </label>
              <input value={isEdit ? form.mch_code : 'Auto-generated on save'}
                onChange={e => isEdit && set('mch_code', e.target.value)}
                readOnly={!isEdit} className={isEdit ? inp : inpRO} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Machine Name *</label>
              <input required value={form.mch_name} onChange={e => set('mch_name',e.target.value)}
                placeholder="JCB 3CX Backhoe Loader" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Machine Type *</label>
              <select required value={form.mch_type} onChange={e => set('mch_type',e.target.value)} className={inp}>
                {MCH_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Owned / Rented *</label>
              <div className="flex gap-4 mt-2">
                {['OWNED','RENTED'].map(v => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="owned_or_rented" value={v}
                      checked={form.owned_or_rented===v} onChange={() => set('owned_or_rented',v)}
                      className="text-blue-600" />
                    <span className="text-sm text-gray-700">{v}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Make / Brand</label>
              <input value={form.make} onChange={e => set('make',e.target.value)}
                placeholder="JCB, Komatsu..." className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Model</label>
              <input value={form.model} onChange={e => set('model',e.target.value)}
                placeholder="3CX, PC200..." className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Capacity / Spec</label>
              <input value={form.capacity_spec} onChange={e => set('capacity_spec',e.target.value)}
                placeholder="0.3 CUM bucket, 15 KVA..." className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Registration Number</label>
              <input value={form.registration_number} onChange={e => set('registration_number',e.target.value)}
                placeholder="KA-01-AB-1234" className={inp} />
            </div>
          </div>
          {isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Status</label>
                <select value={form.current_status} onChange={e => set('current_status',e.target.value)} className={inp}>
                  {['AVAILABLE','ON_SITE','MAINTENANCE','RETIRED'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active}
                    onChange={e => set('is_active',e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Machine' : 'Add Machine')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Machine Detail Modal (Service / Fuel / Breakdown / Ownership)
// ─────────────────────────────────────────────────────────────
const MachineDetailModal = ({ machine, onClose }: { machine: any; onClose: ()=>void }) => {
  const [tab, setTab]             = useState<'service'|'fuel'|'breakdown'|'ownership'>('service');
  const [serviceLogs, setSvcLogs] = useState<any[]>([]);
  const [fuelLogs, setFuelLogs]   = useState<any[]>([]);
  const [bkdLogs, setBkdLogs]     = useState<any[]>([]);
  const [ownership, setOwnership] = useState<any>(null);
  const [fuelSummary, setFuelSum] = useState<any>(null);
  const [showSvc, setShowSvc] = useState(false);
  const [showFuel, setShowFuel] = useState(false);
  const [showBkd, setShowBkd] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [svcForm, setSvcForm]     = useState({
    service_date: new Date().toISOString().split('T')[0],
    service_type:'SCHEDULED', description:'', cost:0,
    vendor_name:'', downtime_days:0, next_service_due:''
  });
  const [fuelForm, setFuelForm]   = useState({
    project_id:'', log_date: new Date().toISOString().split('T')[0],
    litres_filled:'', rate_per_litre:'', hours_run_today:0
  });
  const [bkdForm, setBkdForm]     = useState({
    project_id:'', breakdown_date: new Date().toISOString().split('T')[0],
    description:'', downtime_hours:0, vendor_notified:false
  });
  const [ownForm, setOwnForm]     = useState({
    purchase_date:'', purchase_cost:'', expected_life_years:'',
    expected_life_hours:'', current_hours_run:0, current_book_value:'',
    insurance_policy_no:'', insurance_expiry:'', resale_value_estimated:''
  });
  const [error, setError]         = useState('');

  const loadTab = async (t: string) => {
    const { default: api } = await import('../../utils/api');
    try {
      if (t === 'service') {
        const r = await api.get(`/machinery/${machine.mch_id}/service`);
        setSvcLogs(r.data.data);
      } else if (t === 'fuel') {
        const [f, s] = await Promise.all([
          api.get(`/machinery/${machine.mch_id}/fuel`),
          api.get(`/machinery/${machine.mch_id}/fuel-summary`)
        ]);
        setFuelLogs(f.data.data); setFuelSum(s.data.data);
      } else if (t === 'breakdown') {
        const r = await api.get(`/machinery/${machine.mch_id}/breakdown`);
        setBkdLogs(r.data.data);
      } else if (t === 'ownership') {
        const r = await api.get(`/machinery/${machine.mch_id}/ownership`);
        const d = r.data.data;
        setOwnership(d);
        if (d) setOwnForm({
          purchase_date: d.purchase_date?.split('T')[0]||'',
          purchase_cost: d.purchase_cost||'',
          expected_life_years: d.expected_life_years||'',
          expected_life_hours: d.expected_life_hours||'',
          current_hours_run: d.current_hours_run||0,
          current_book_value: d.current_book_value||'',
          insurance_policy_no: d.insurance_policy_no||'',
          insurance_expiry: d.insurance_expiry?.split('T')[0]||'',
          resale_value_estimated: d.resale_value_estimated||''
        });
      }
    } catch {}
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const saveService = async () => {
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/machinery/${machine.mch_id}/service`, svcForm);
      setShowSvc(false); loadTab('service');
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const saveFuel = async () => {
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/machinery/${machine.mch_id}/fuel`, fuelForm);
      setShowFuel(false); loadTab('fuel');
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const saveBreakdown = async () => {
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/machinery/${machine.mch_id}/breakdown`, bkdForm);
      setShowBkd(false); loadTab('breakdown');
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const saveOwnership = async () => {
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/machinery/${machine.mch_id}/ownership`, ownForm);
      loadTab('ownership');
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const tabs = [
    { key:'service',   label:'Service Log' },
    { key:'fuel',      label:'Fuel Log' },
    { key:'breakdown', label:'Breakdowns' },
    { key:'ownership', label:'Ownership' },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">{machine.mch_name}</h3>
            <p className="text-xs text-gray-400">{machine.mch_code} · {machine.mch_type}
              {machine.current_project_name && ` · 📍 ${machine.current_project_name}`}
            </p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        {/* Sub-tabs */}
        <div className="flex gap-1 px-6 pt-3 pb-0 border-b border-gray-100">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs font-medium rounded-t-lg transition-colors
                ${tab===t.key ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        {error && <div className="mx-6 mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

        <div className="flex-1 overflow-y-auto p-6">

          {/* ── SERVICE LOG ── */}
          {tab === 'service' && (
            <>
              <div className="flex justify-between mb-3">
                <p className="text-sm text-gray-500">{serviceLogs.length} service records</p>
                <button onClick={() => setShowSvc(!showSvc)}
                  className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
                  <Plus size={12}/> Add Service
                </button>
              </div>
              {showSvc && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Service Date *</label>
                      <input type="date" value={svcForm.service_date}
                        onChange={e => setSvcForm(f=>({...f,service_date:e.target.value}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Service Type *</label>
                      <select value={svcForm.service_type}
                        onChange={e => setSvcForm(f=>({...f,service_type:e.target.value}))} className={inp}>
                        {SERVICE_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Description *</label>
                    <textarea rows={2} value={svcForm.description}
                      onChange={e => setSvcForm(f=>({...f,description:e.target.value}))} className={inp} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Cost (₹)</label>
                      <input type="number" value={svcForm.cost}
                        onChange={e => setSvcForm(f=>({...f,cost:Number(e.target.value)}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Downtime Days</label>
                      <input type="number" value={svcForm.downtime_days}
                        onChange={e => setSvcForm(f=>({...f,downtime_days:Number(e.target.value)}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Next Service Due</label>
                      <input type="date" value={svcForm.next_service_due}
                        onChange={e => setSvcForm(f=>({...f,next_service_due:e.target.value}))} className={inp} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Vendor Name</label>
                      <input value={svcForm.vendor_name}
                        onChange={e => setSvcForm(f=>({...f,vendor_name:e.target.value}))} className={inp} />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowSvc(false)}
                      className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button onClick={saveService} disabled={saving}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg disabled:opacity-50">
                      {saving ? 'Saving...' : 'Save Service'}
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {serviceLogs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No service records yet.</p>
                ) : serviceLogs.map(s => (
                  <div key={s.service_id} className="border border-gray-100 rounded-xl p-3 bg-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium mr-2
                          ${s.service_type==='BREAKDOWN' ? 'bg-red-100 text-red-700' :
                            s.service_type==='SCHEDULED' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'}`}>
                          {s.service_type}
                        </span>
                        <span className="text-xs text-gray-400">{fmtDate(s.service_date)}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">₹{fmtN(s.cost)}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{s.description}</p>
                    <div className="flex gap-4 mt-1 text-xs text-gray-400">
                      {s.downtime_days > 0 && <span>⏱ {s.downtime_days}d downtime</span>}
                      {s.next_service_due && <span>Next: {fmtDate(s.next_service_due)}</span>}
                      {s.vendor_name && <span>Vendor: {s.vendor_name}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── FUEL LOG ── */}
          {tab === 'fuel' && (
            <>
              {fuelSummary && fuelSummary.total_fills > 0 && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {[
                    { label:'Total Litres', val: `${fmtN(fuelSummary.total_litres)} L` },
                    { label:'Total Cost', val: `₹${fmtN(fuelSummary.total_fuel_cost)}` },
                    { label:'Total Hours', val: `${fmtN(fuelSummary.total_hours)} hrs` },
                    { label:'L/Hour', val: fuelSummary.litres_per_hour||'—' },
                  ].map(s => (
                    <div key={s.label} className="bg-blue-50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-blue-700">{s.val}</p>
                      <p className="text-xs text-blue-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between mb-3">
                <p className="text-sm text-gray-500">{fuelLogs.length} fuel records</p>
                <button onClick={() => setShowFuel(!showFuel)}
                  className="flex items-center gap-1 text-xs bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600">
                  <Plus size={12}/> Add Fuel
                </button>
              </div>
              {showFuel && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Date *</label>
                      <input type="date" value={fuelForm.log_date}
                        onChange={e => setFuelForm(f=>({...f,log_date:e.target.value}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Project ID *</label>
                      <input type="number" value={fuelForm.project_id}
                        onChange={e => setFuelForm(f=>({...f,project_id:e.target.value}))} className={inp} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Litres Filled *</label>
                      <input type="number" step="0.1" value={fuelForm.litres_filled}
                        onChange={e => setFuelForm(f=>({...f,litres_filled:e.target.value}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Rate/Litre (₹) *</label>
                      <input type="number" step="0.01" value={fuelForm.rate_per_litre}
                        onChange={e => setFuelForm(f=>({...f,rate_per_litre:e.target.value}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Hours Run Today</label>
                      <input type="number" step="0.5" value={fuelForm.hours_run_today}
                        onChange={e => setFuelForm(f=>({...f,hours_run_today:Number(e.target.value)}))} className={inp} />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowFuel(false)}
                      className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button onClick={saveFuel} disabled={saving}
                      className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg disabled:opacity-50">
                      {saving ? 'Saving...' : 'Save Fuel Log'}
                    </button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Litres</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Rate/L</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Cost</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fuelLogs.length === 0 ? (
                      <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No fuel records yet.</td></tr>
                    ) : fuelLogs.map(f => (
                      <tr key={f.fuel_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600">{fmtDate(f.log_date)}</td>
                        <td className="px-3 py-2 font-medium">{f.litres_filled} L</td>
                        <td className="px-3 py-2 text-gray-500">₹{f.rate_per_litre}</td>
                        <td className="px-3 py-2 font-semibold text-gray-800">₹{fmtN(f.fuel_cost)}</td>
                        <td className="px-3 py-2 text-gray-500">{f.hours_run_today} hrs</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ── BREAKDOWN LOG ── */}
          {tab === 'breakdown' && (
            <>
              <div className="flex justify-between mb-3">
                <p className="text-sm text-gray-500">{bkdLogs.length} breakdown records</p>
                <button onClick={() => setShowBkd(!showBkd)}
                  className="flex items-center gap-1 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700">
                  <AlertTriangle size={12}/> Report Breakdown
                </button>
              </div>
              {showBkd && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Date *</label>
                      <input type="date" value={bkdForm.breakdown_date}
                        onChange={e => setBkdForm(f=>({...f,breakdown_date:e.target.value}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Project ID *</label>
                      <input type="number" value={bkdForm.project_id}
                        onChange={e => setBkdForm(f=>({...f,project_id:e.target.value}))} className={inp} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Description *</label>
                    <textarea rows={2} value={bkdForm.description}
                      onChange={e => setBkdForm(f=>({...f,description:e.target.value}))} className={inp} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Downtime Hours</label>
                      <input type="number" step="0.5" value={bkdForm.downtime_hours}
                        onChange={e => setBkdForm(f=>({...f,downtime_hours:Number(e.target.value)}))} className={inp} />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={bkdForm.vendor_notified}
                          onChange={e => setBkdForm(f=>({...f,vendor_notified:e.target.checked}))}
                          className="w-4 h-4 rounded" />
                        <span className="text-sm text-gray-700">Vendor notified</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowBkd(false)}
                      className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button onClick={saveBreakdown} disabled={saving}
                      className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg disabled:opacity-50">
                      {saving ? 'Saving...' : 'Report Breakdown'}
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {bkdLogs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No breakdown records. 🎉</p>
                ) : bkdLogs.map(b => (
                  <div key={b.breakdown_id} className="border border-red-100 rounded-xl p-3 bg-red-50">
                    <div className="flex justify-between">
                      <span className="text-xs font-medium text-red-700">{fmtDate(b.breakdown_date)}</span>
                      <span className="text-xs text-red-600">{b.downtime_hours} hrs downtime</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{b.description}</p>
                    <div className="flex gap-3 mt-1 text-xs text-gray-400">
                      {b.repair_completed_date && <span>✅ Repaired: {fmtDate(b.repair_completed_date)}</span>}
                      {b.vendor_notified && <span>📞 Vendor notified</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── OWNERSHIP ── */}
          {tab === 'ownership' && machine.owned_or_rented === 'OWNED' && (
            <div className="space-y-3">
              {ownership && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label:'Purchase Cost', val:`₹${fmtN(ownership.purchase_cost)}` },
                    { label:'Book Value', val:`₹${fmtN(ownership.current_book_value)}` },
                    { label:'Annual Depreciation', val:`₹${fmtN(ownership.annual_depreciation)}` },
                    { label:'Hours Run', val:`${fmtN(ownership.current_hours_run)} hrs` },
                    { label:'Expected Life', val:`${ownership.expected_life_years} yrs` },
                    { label:'Insurance Expiry', val:fmtDate(ownership.insurance_expiry) },
                  ].map(s => (
                    <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-sm font-bold text-gray-800">{s.val}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <h4 className="text-xs font-semibold text-gray-500 mb-2">
                {ownership ? 'Update Ownership Details' : 'Add Ownership Details'}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Purchase Date *</label>
                  <input type="date" value={ownForm.purchase_date}
                    onChange={e => setOwnForm(f=>({...f,purchase_date:e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Purchase Cost (₹) *</label>
                  <input type="number" value={ownForm.purchase_cost}
                    onChange={e => setOwnForm(f=>({...f,purchase_cost:e.target.value}))} className={inp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Expected Life (years) *</label>
                  <input type="number" value={ownForm.expected_life_years}
                    onChange={e => setOwnForm(f=>({...f,expected_life_years:e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Expected Life (hours)</label>
                  <input type="number" value={ownForm.expected_life_hours}
                    onChange={e => setOwnForm(f=>({...f,expected_life_hours:e.target.value}))} className={inp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Current Hours Run</label>
                  <input type="number" step="0.5" value={ownForm.current_hours_run}
                    onChange={e => setOwnForm(f=>({...f,current_hours_run:Number(e.target.value)}))} className={inp} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Current Book Value (₹)</label>
                  <input type="number" value={ownForm.current_book_value}
                    onChange={e => setOwnForm(f=>({...f,current_book_value:e.target.value}))} className={inp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Insurance Policy No</label>
                  <input value={ownForm.insurance_policy_no}
                    onChange={e => setOwnForm(f=>({...f,insurance_policy_no:e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Insurance Expiry</label>
                  <input type="date" value={ownForm.insurance_expiry}
                    onChange={e => setOwnForm(f=>({...f,insurance_expiry:e.target.value}))} className={inp} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Estimated Resale Value (₹)</label>
                <input type="number" value={ownForm.resale_value_estimated}
                  onChange={e => setOwnForm(f=>({...f,resale_value_estimated:e.target.value}))} className={inp} />
              </div>
              <div className="flex justify-end">
                <button onClick={saveOwnership} disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : (ownership ? 'Update Ownership' : 'Save Ownership')}
                </button>
              </div>
            </div>
          )}
          {tab === 'ownership' && machine.owned_or_rented === 'RENTED' && (
            <div className="text-center py-12 text-gray-400">
              Ownership details only apply to OWNED machines.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Update Status Modal ───────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const UpdateStatusModal = ({ machine, onClose, onSaved }: any) => {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(machine.current_status);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      await machineryService.updateStatus(machine.mch_id, status);
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">Update Status</h3>
            <p className="text-xs text-gray-400">{machine.mch_name}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {['AVAILABLE','ON_SITE','MAINTENANCE','RETIRED'].map(s => (
              <button key={s} type="button" onClick={() => setStatus(s)}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all
                  ${status===s ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Vendor Modal ──────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const VendorModal = ({ vendor, onClose, onSaved }: {
  vendor?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!vendor;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [subtab, setSubtab] = useState<'basic'|'bank'>('basic');
  const [form, setForm]     = useState({
    vnd_code:       vendor?.vnd_code       || '',
    company_name:   vendor?.company_name   || '',
    contact_person: vendor?.contact_person || '',
    mobile:         vendor?.mobile         || '',
    email:          vendor?.email          || '',
    gstin:          vendor?.gstin          || '',
    address:        vendor?.address        || '',
    city:           vendor?.city           || 'Bengaluru',
    state:          vendor?.state          || 'Karnataka',
    payment_terms:  vendor?.payment_terms  || 'NET_15',
    service_areas:  vendor?.service_areas  || '',
    rating:         vendor?.rating         || 0,
    bank_account:   vendor?.bank_account   || '',
    bank_ifsc:      vendor?.bank_ifsc      || '',
    bank_name:      vendor?.bank_name      || '',
    is_active:      vendor?.is_active !== undefined ? vendor.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f=>({...f,[k]:v}));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) { await api.put(`/machinery/vendors/${vendor.vnd_id}`, form); }
      else        { await api.post('/machinery/vendors', form); }
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Vendor' : 'Add Vendor'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <div className="flex gap-1 px-6 pt-4">
          {(['basic','bank'] as const).map(t => (
            <button key={t} type="button" onClick={() => setSubtab(t)}
              className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${subtab===t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t === 'basic' ? 'Basic Info' : 'Bank Details'}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          {subtab === 'basic' && <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">
                  Vendor Code {!isEdit && <span className="text-gray-400 font-normal">(auto)</span>}
                </label>
                <input value={isEdit ? form.vnd_code : 'Auto-generated'}
                  onChange={e => isEdit && set('vnd_code',e.target.value)}
                  readOnly={!isEdit} className={isEdit ? inp : inpRO} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Company Name *</label>
                <input required value={form.company_name} onChange={e => set('company_name',e.target.value)} className={inp} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Contact Person *</label>
                <input required value={form.contact_person} onChange={e => set('contact_person',e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Mobile *</label>
                <input required value={form.mobile} onChange={e => set('mobile',e.target.value)} className={inp} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => set('email',e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">GSTIN</label>
                <input value={form.gstin} onChange={e => set('gstin',e.target.value)} placeholder="29XXXXX1234F1Z5" className={inp} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Address</label>
              <textarea rows={2} value={form.address} onChange={e => set('address',e.target.value)} className={inp} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">City</label>
                <input value={form.city} onChange={e => set('city',e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">State</label>
                <input value={form.state} onChange={e => set('state',e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Payment Terms</label>
                <select value={form.payment_terms} onChange={e => set('payment_terms',e.target.value)} className={inp}>
                  {VND_PAYMENT_TERMS.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Service Areas</label>
              <input value={form.service_areas} onChange={e => set('service_areas',e.target.value)}
                placeholder="Whitefield, Sarjapur, HSR Layout..." className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Rating</label>
              <StarRating value={form.rating} onChange={v => set('rating',v)} />
            </div>
            {isEdit && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active',e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">Active Vendor</span>
              </label>
            )}
          </>}
          {subtab === 'bank' && <>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 mb-2">
              Bank details used for payment processing. Keep confidential.
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Bank Name</label>
              <input value={form.bank_name} onChange={e => set('bank_name',e.target.value)}
                placeholder="State Bank of India" className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Account Number</label>
              <input value={form.bank_account} onChange={e => set('bank_account',e.target.value)} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">IFSC Code</label>
              <input value={form.bank_ifsc} onChange={e => set('bank_ifsc',e.target.value)}
                placeholder="SBIN0001234" className={inp} />
            </div>
          </>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Vendor' : 'Add Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Fleet Tab ─────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const FleetTab = () => {
  const [machines, setMachines]       = useState<any[]>([]);
  const [stats, setStats]             = useState<any>(null);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatus]     = useState('');
  const [typeFilter, setType]         = useState('');
  const [showInactive, setInactive]   = useState(false);
  const [showAdd, setShowAdd]         = useState(false);
  const [editMachine, setEditMachine] = useState<any>(null);
  const [statusModal, setStatusModal] = useState<any>(null);
  const [detailModal, setDetailModal] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([
        machineryService.getAll({ status: statusFilter||undefined, mch_type: typeFilter||undefined, search: search||undefined }),
        machineryService.getStats(),
      ]);
      let data = mRes.data.data;
      if (!showInactive) data = data.filter((m:any) => m.is_active !== false);
      setMachines(data); setStats(sRes.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, typeFilter, showInactive]);

  // Alert checks
  const isServiceDue = (m: any) => m.next_service_due &&
    new Date(m.next_service_due) <= new Date(Date.now() + 7*24*60*60*1000);
  const isInsuranceExpiring = (m: any) => m.insurance_expiry &&
    new Date(m.insurance_expiry) <= new Date(Date.now() + 30*24*60*60*1000);

  return (
    <>
      {showAdd && <MachineModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editMachine && <MachineModal machine={editMachine} onClose={() => setEditMachine(null)}
        onSaved={() => { setEditMachine(null); load(); }} />}
      {statusModal && <UpdateStatusModal machine={statusModal} onClose={() => setStatusModal(null)}
        onSaved={() => { setStatusModal(null); load(); }} />}
      {detailModal && <MachineDetailModal machine={detailModal} onClose={() => setDetailModal(null)} />}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {stats ? `${fmtN(stats.total)} machines · ${fmtN(stats.available)} available · ${fmtN(stats.owned)} owned / ${fmtN(stats.rented)} rented` : ''}
          </p>
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500">
            <input type="checkbox" checked={showInactive} onChange={e => setInactive(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300" />
            Show inactive
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportCSV(machines.map(m=>({
            Code:m.mch_code,Name:m.mch_name,Type:m.mch_type,Make:m.make||'',
            Model:m.model||'',Ownership:m.owned_or_rented,Status:m.current_status,
            Registration:m.registration_number||''
          })), 'machinery.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Machine
          </button>
        </div>
      </div>

      {/* Alerts */}
      {stats && (Number(stats.service_due_soon) > 0 || Number(stats.insurance_expiring) > 0) && (
        <div className="flex gap-3 mb-4 flex-wrap">
          {Number(stats.service_due_soon) > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              <AlertTriangle size={14}/> {stats.service_due_soon} machine(s) due for service within 7 days
            </div>
          )}
          {Number(stats.insurance_expiring) > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
              <AlertTriangle size={14}/> {stats.insurance_expiring} machine(s) insurance expiring within 30 days
            </div>
          )}
        </div>
      )}

      {/* Status filter */}
      <div className="flex flex-wrap gap-2 mb-3">
        {['','AVAILABLE','ON_SITE','MAINTENANCE','RETIRED'].map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${statusFilter===s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
            {s || 'All Status'}
          </button>
        ))}
      </div>

      {/* Type filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['','JCB','CONCRETE_MIXER','VIBRATOR','GENERATOR','COMPACTOR','EXCAVATOR','DUMPER','OTHER'].map(t => (
          <button key={t} onClick={() => setType(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${typeFilter===t ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}>
            {t || 'All Types'}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={e => { e.preventDefault(); load(); }} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search machines..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Search</button>
      </form>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? [...Array(6)].map((_,i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="bg-gray-100 rounded h-5 w-2/3 mb-2"/>
            <div className="bg-gray-100 rounded h-4 w-1/2"/>
          </div>
        )) : machines.map(m => (
          <div key={m.mch_id}
            className={`bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow
              ${m.is_active===false ? 'opacity-50' : ''}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-gray-900 text-sm">{m.mch_name}</p>
                <p className="text-xs text-gray-400">{m.mch_code}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.current_status]||''}`}>
                  {m.current_status}
                </span>
                {(isServiceDue(m) || isInsuranceExpiring(m)) && (
                  <span className="text-xs text-red-500 flex items-center gap-0.5">
                    <AlertTriangle size={10}/> Alert
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-1 rounded">{m.mch_type}</span>
              <span className={`text-xs px-2 py-1 rounded border font-medium
                ${m.owned_or_rented==='OWNED' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-orange-50 border-orange-200 text-orange-700'}`}>
                {m.owned_or_rented}
              </span>
            </div>
            {m.make && <p className="text-xs text-gray-400 mt-2">{m.make} {m.model}{m.capacity_spec ? ` · ${m.capacity_spec}` : ''}</p>}
            {m.registration_number && <p className="text-xs text-gray-400">{m.registration_number}</p>}
            {m.current_project_name && (
              <p className="text-xs text-blue-500 mt-1">📍 {m.current_project_name}</p>
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={() => setStatusModal(m)}
                className="flex-1 text-xs text-blue-600 border border-blue-200 rounded-lg py-1.5 hover:bg-blue-50">
                Status
              </button>
              <button onClick={() => setEditMachine(m)}
                className="px-3 text-xs text-gray-600 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50">
                <Edit2 size={12}/>
              </button>
              <button onClick={() => setDetailModal(m)}
                className="px-3 text-xs text-gray-600 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50">
                <Settings size={12}/>
              </button>
            </div>
          </div>
        ))}
      </div>
      {!loading && machines.length === 0 && (
        <div className="text-center py-12 text-gray-400">No machines found.</div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Vendors Tab ───────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const VendorsTab = () => {
  const [vendors, setVendors]       = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [editVendor, setEditVendor] = useState<any>(null);
  const [approving, setApproving]   = useState<number|null>(null);
  const [rateModal, setRateModal]   = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.get('/machinery/vendors/all');
      setVendors(r.data.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (vndId: number) => {
    setApproving(vndId);
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/machinery/vendors/${vndId}/approve`);
      load();
    } finally { setApproving(null); }
  };

  return (
    <>
      {showAdd && <VendorModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editVendor && <VendorModal vendor={editVendor} onClose={() => setEditVendor(null)}
        onSaved={() => { setEditVendor(null); load(); }} />}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{vendors.length} machinery vendors</p>
        <div className="flex gap-2">
          <button onClick={() => exportCSV(vendors.map(v=>({
            Code:v.vnd_code,Company:v.company_name,Contact:v.contact_person,
            Mobile:v.mobile,City:v.city,Status:v.registration_status,Rating:v.rating||0
          })),'vendors.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Vendor
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Contact</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">City</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Payment</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Rating</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Rates</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(3)].map((_,i) => (
              <tr key={i}>{[...Array(8)].map((_,j) => (
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse"/></td>
              ))}</tr>
            )) : vendors.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                No vendors yet. Add machinery hire vendors.
              </td></tr>
            ) : vendors.map(v => (
              <tr key={v.vnd_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{v.company_name}</div>
                  <div className="text-xs text-gray-400">{v.vnd_code}</div>
                  {v.service_areas && <div className="text-xs text-blue-500">📍 {v.service_areas}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="text-gray-700">{v.contact_person}</div>
                  <div className="text-xs text-gray-400">{v.mobile}</div>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">{v.city}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{v.payment_terms?.replace('_',' ')}</td>
                <td className="px-4 py-3"><StarRating value={v.rating||0} readonly /></td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-500">{v.rate_count||0} rates</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                    ${v.registration_status==='APPROVED' ? 'bg-green-100 text-green-700' :
                      v.registration_status==='PENDING' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'}`}>
                    {v.registration_status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => setEditVendor(v)}
                      className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg hover:bg-blue-100">
                      <Edit2 size={11}/> Edit
                    </button>
                    {v.registration_status !== 'APPROVED' && (
                      <button onClick={() => approve(v.vnd_id)} disabled={approving===v.vnd_id}
                        className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg hover:bg-green-100 disabled:opacity-50">
                        <Check size={11}/> {approving===v.vnd_id ? '...' : 'Approve'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Activity Mapping Tab ──────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const ActivityMappingTab = () => {
  const [mappings, setMappings]   = useState<any[]>([]);
  const [filtered, setFiltered]   = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get('/machinery/activity-mappings');
        setMappings(r.data.data||[]); setFiltered(r.data.data||[]);
      } catch { setMappings([]); setFiltered([]); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    let data = mappings;
    if (typeFilter) data = data.filter((m:any) => m.mch_type === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((m:any) =>
        m.activity_name?.toLowerCase().includes(q) ||
        m.act_code?.toLowerCase().includes(q) ||
        m.mch_type?.toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, typeFilter, mappings]);

  const grouped = filtered.reduce((acc:any, row:any) => {
    const key = row.act_code;
    if (!acc[key]) acc[key] = { act_code:row.act_code, activity_name:row.activity_name, items:[] };
    acc[key].items.push(row);
    return acc;
  }, {});

  const mchTypes = mappings.map((m:any) => m.mch_type)
    .filter((t:string, i:number, arr:string[]) => arr.indexOf(t) === i).sort();

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{mappings.length} activity-machinery mappings</p>
        <button onClick={() => exportCSV(mappings.map(m=>({
          ActivityCode:m.act_code, ActivityName:m.activity_name,
          MachineType:m.mch_type, HoursPerDay:m.hours_per_day,
          QtyNeeded:m.quantity_needed, OwnedOrHired:m.owned_or_hired,
          Mandatory:m.is_mandatory?'Yes':'No'
        })),'activity_machinery_map.csv')}
          className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
          <Download size={14}/> Export
        </button>
      </div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search activity or machine type..."
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"/>
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Machine Types</option>
          {mchTypes.map(t => <option key={t as string}>{t as string}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_,i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="bg-gray-100 rounded h-4 w-48 mb-2"/>
              <div className="bg-gray-100 rounded h-3 w-32"/>
            </div>
          ))}
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 text-gray-400">No mappings found.</div>
      ) : (
        <div className="space-y-2">
          {Object.values(grouped).map((grp:any) => (
            <div key={grp.act_code} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded mr-2">
                    {grp.act_code}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{grp.activity_name}</span>
                </div>
                <span className="text-xs text-gray-400">{grp.items.length} machine type{grp.items.length>1?'s':''}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {grp.items.map((m:any, i:number) => (
                  <div key={i} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs border
                    ${m.is_mandatory ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                    <Wrench size={11} className="text-gray-400"/>
                    <span className="font-medium text-gray-800">{m.mch_type}</span>
                    <span className="text-gray-500">×{m.quantity_needed}</span>
                    <span className="text-gray-400">{m.hours_per_day}h/day</span>
                    {m.owned_or_hired !== 'EITHER' && (
                      <span className="text-blue-500">{m.owned_or_hired}</span>
                    )}
                    {m.is_mandatory && <span className="text-red-600 font-bold">*</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Summary Tab ───────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const SummaryTab = () => {
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    machineryService.getStats().then(r => { setStats(r.data.data); setLoading(false); });
  }, []);
  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!stats) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label:'Total Fleet',  value:fmtN(stats.total),       color:'bg-blue-600 text-white' },
          { label:'Available',    value:fmtN(stats.available),   color:'bg-green-600 text-white' },
          { label:'On Site',      value:fmtN(stats.on_site),     color:'bg-amber-500 text-white' },
          { label:'Maintenance',  value:fmtN(stats.maintenance), color:'bg-red-500 text-white' },
          { label:'Retired',      value:fmtN(stats.retired),     color:'bg-gray-500 text-white' },
        ].map(c => (
          <div key={c.label} className={`${c.color} rounded-xl p-4 text-center`}>
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs font-medium mt-1 opacity-90">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Owned vs Rented</h3>
          <div className="flex gap-4">
            <div className="flex-1 bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{fmtN(stats.owned)}</p>
              <p className="text-xs text-green-600 mt-1">Owned</p>
            </div>
            <div className="flex-1 bg-orange-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">{fmtN(stats.rented)}</p>
              <p className="text-xs text-orange-600 mt-1">Rented</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Alerts</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Service due (7 days)</span>
              <span className={`text-sm font-bold ${Number(stats.service_due_soon)>0 ? 'text-amber-600' : 'text-green-600'}`}>
                {fmtN(stats.service_due_soon)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Insurance expiring (30 days)</span>
              <span className={`text-sm font-bold ${Number(stats.insurance_expiring)>0 ? 'text-red-600' : 'text-green-600'}`}>
                {fmtN(stats.insurance_expiring)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {stats.by_type && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Fleet by Machine Type</h3>
          <div className="space-y-2">
            {Object.entries(stats.by_type).map(([type, cnt]: any) => (
              <div key={type} className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-44">{type.replace('_',' ')}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${(cnt/stats.total)*100}%` }} />
                </div>
                <span className="text-sm font-semibold text-gray-700 w-6 text-right">{cnt}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Main MachineryPage ────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const MachineryPage = () => {
  const [tab, setTab] = useState<'fleet'|'vendors'|'mapping'|'summary'>('fleet');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Machinery Master</h1>
        <p className="text-sm text-gray-500 mt-0.5">Fleet · Vendors · Service · Fuel · Activity Mapping</p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          { key:'fleet',   label:'Fleet',            icon:<Wrench size={14}/> },
          { key:'vendors', label:'Vendors',           icon:<Building2 size={14}/> },
          { key:'mapping', label:'Activity Mapping',  icon:<Activity size={14}/> },
          { key:'summary', label:'Summary',           icon:<Settings size={14}/> },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
      {tab === 'fleet'   && <FleetTab />}
      {tab === 'vendors' && <VendorsTab />}
      {tab === 'mapping' && <ActivityMappingTab />}
      {tab === 'summary' && <SummaryTab />}
    </div>
  );
};

export default MachineryPage;
